import { podeVerTodos } from '@/features/auth/perfil-de-acesso';
import { listContacts, type ContactRow } from '@/features/contatos/api';
import { listStartableChannels, type StartableChannel } from '@/features/channels/api';
import { listDepartments, listTemplates, type TemplateRow } from '@/features/configuracoes/api';
import { getMediaUrlById, type LibraryMedia } from '@/features/media-library/api';
import { MidiaDoCabecalhoPicker } from '@/features/media-library/MidiaDoCabecalhoPicker';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ArrowRight,
  ChevronDown,
  ExternalLink,
  Loader2,
  Phone,
  Reply,
  Search,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { type ActiveOutboundInput, checkWhatsAppNumber, createActiveOutbound } from '../api';
import {
  type BotaoTemplate,
  botoesDinamicosDeUrl,
  botoesDoTemplate,
  prefixoDaUrl,
} from '../lib/template-botoes';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (ticketId: number) => void;
}

// DDIs mais comuns — exibe "+código Label"
const DDI_LIST = [
  { code: '55', label: 'Brasil' },
  { code: '1', label: 'EUA / Canadá' },
  { code: '351', label: 'Portugal' },
  { code: '44', label: 'Reino Unido' },
  { code: '34', label: 'Espanha' },
  { code: '54', label: 'Argentina' },
  { code: '52', label: 'México' },
  { code: '57', label: 'Colômbia' },
  { code: '56', label: 'Chile' },
  { code: '598', label: 'Uruguai' },
];

function maskPhone(digits: string, ddi: string): string {
  if (ddi === '55') {
    // Máscara brasileira: (XX) XXXXX-XXXX ou (XX) XXXX-XXXX
    const d = digits.slice(0, 11);
    if (d.length <= 2) return d;
    if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
    if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  }
  // Genérico: sem máscara específica
  return digits.slice(0, 15);
}

/** Lista vazia FIXA pro seletor abaixo. No zustand v5 o seletor não pode
 *  devolver referência nova a cada leitura: `?? []` inline com user=null
 *  (sessão limpa quando o refresh demora >5s) entrava em loop e derrubava a
 *  tela inteira com o React #185 (Drogaria Salutem, 20/out/2026). */
const SEM_DEPARTAMENTOS: number[] = [];

export function NewActiveOutboundModal({ open, onClose, onCreated }: Props) {
  const qc = useQueryClient();
  // Fechar (X, Cancelar, Esc) sempre limpa o formulário — reabrir não pode
  // trazer canal/contato/template da tentativa anterior. Clicar fora NÃO fecha.
  const fechar = (): void => {
    reset();
    onClose();
  };
  useEscapeClose(open, fechar);

  // --- Telefone ---
  const [ddi, setDdi] = useState('55');
  const [phoneDisplay, setPhoneDisplay] = useState(''); // valor mascarado
  const phoneDigits = phoneDisplay.replace(/\D/g, '');
  const waId = ddi + phoneDigits; // E.164 sem +

  // --- Contato ---
  // contactInput é o texto do campo (nome livre ou busca)
  // selectedContact é preenchido apenas quando usuário clica num resultado existente
  const [contactInput, setContactInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedContact, setSelectedContact] = useState<{ id: number; name: string } | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // --- Canal, template, mensagem ---
  const [whatsappNumberId, setWhatsappNumberId] = useState<number | null>(null);
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [body, setBody] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateRow | null>(null);
  // Mídia do cabeçalho deste envio (template com header de mídia): escolhida
  // da galeria ou enviada do computador na hora — não há arte padrão.
  const [headerMedia, setHeaderMedia] = useState<LibraryMedia | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [existingTicketId, setExistingTicketId] = useState<number | null>(null);
  // "Ver chat" só quando dá pra abrir: atendimento em aberto de outro agente
  // fica fechado pra quem não tem "Visualizar todos" (o servidor recusa).
  const [verChatLiberado, setVerChatLiberado] = useState(true);
  const veColegas = useAuthStore((s) => podeVerTodos(s.user));

  // --- Departamentos do user (controle de acesso) ---
  const userDeptIds = useAuthStore((s) => s.user?.departmentIds ?? SEM_DEPARTAMENTOS);
  // Support (login de suporte impersonando o tenant) não é membro de nenhum
  // dept do cliente, mas o backend libera escolher QUALQUER dept (isSupport).
  // Então pro support mostramos todos os depts do tenant em vez da interseção.
  const isSupport = useAuthStore((s) => Boolean(s.user?.impersonating));
  const deptsQ = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
    enabled: open && (userDeptIds.length > 0 || isSupport),
  });
  /** Depts disponíveis: interseção com os do user; pro support, TODOS do tenant. */
  const availableDepts = useMemo(() => {
    const all = deptsQ.data ?? [];
    return isSupport ? all : all.filter((d) => userDeptIds.includes(d.id));
  }, [deptsQ.data, userDeptIds, isSupport]);

  // Auto-seleciona se user tem só 1 dept (UX — evita clique desnecessário).
  useEffect(() => {
    if (!open) return;
    if (departmentId !== null) return;
    if (availableDepts.length === 1 && availableDepts[0]) {
      setDepartmentId(availableDepts[0].id);
    }
  }, [open, availableDepts, departmentId]);

  // --- Validação WhatsApp (QR Code) — feita no submit ---
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);

  // --- Queries ---
  // Só os canais que ESTE user pode usar pra iniciar atendimento (vínculo
  // user_whatsapp_numbers). Vazio = sem permissão → não consegue iniciar.
  const channelsQ = useQuery({
    queryKey: ['startable-channels'],
    queryFn: listStartableChannels,
    enabled: open,
  });

  const selectedChannel = useMemo<StartableChannel | null>(() => {
    if (!whatsappNumberId) return null;
    return channelsQ.data?.find((c) => c.id === whatsappNumberId) ?? null;
  }, [whatsappNumberId, channelsQ.data]);

  const contactsQ = useQuery({
    queryKey: ['contacts-search', contactInput],
    // 30, não 6: buscar por um pedaço de telefone casa com muito contato, e
    // com seis o certo quase nunca aparecia. O backend aceita até 500 — o teto
    // aqui é só pra lista não ficar pesada de rolar.
    queryFn: () => listContacts({ q: contactInput, limit: 30 }),
    enabled: open && !selectedContact && contactInput.length >= 2,
    staleTime: 10_000,
  });

  const templatesQ = useQuery({
    // Filtra por canal — templates são por WABA e cada canal pertence a 1 WABA.
    // Sem o filtro, picker mostrava templates de OUTRAS WABAs (que a Meta
    // rejeitaria se tentassem enviar).
    queryKey: ['templates', 'approved', whatsappNumberId ?? null],
    queryFn: () => listTemplates('approved', whatsappNumberId ?? undefined),
    enabled: open && selectedChannel?.channelType === 'meta_official' && !!whatsappNumberId,
  });

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const mut = useMutation({
    mutationFn: (input: ActiveOutboundInput) => createActiveOutbound(input),
    onSuccess: ({ ticket_id }) => {
      qc.invalidateQueries({ queryKey: ['tickets'] });
      onCreated(ticket_id);
      reset();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'CONTACT_HAS_ACTIVE_TICKET') {
        const ctx = err.problem.context ?? {};
        const ticketId = ctx.existing_ticket_id as number | undefined;
        const isSameAgent = ctx.is_same_agent as boolean | undefined;

        if (isSameAgent && ticketId) {
          // Mesmo agente — leva direto pra conversa existente (menos cliques),
          // mas AVISA: sem o toast o atendente não percebia que a mensagem/
          // template NÃO foi enviada e achava que tinha disparado.
          const proto = ctx.protocol as string | undefined;
          toast.info(
            proto ? `Atendimento #${proto} já está aberto` : 'Atendimento já está aberto',
            {
              description:
                'Te levamos pra conversa existente. A mensagem NÃO foi enviada — envie por aqui.',
            },
          );
          onCreated(ticketId);
          reset();
          return;
        }

        setExistingTicketId(ticketId ?? null);
        setVerChatLiberado(ctx.status !== 'active' || veColegas || isSupport);
        mostrarErro(err.problem.detail ?? err.message);
      } else {
        mostrarErro(err instanceof Error ? err.message : 'Falha ao criar atendimento');
      }
    },
  });

  function reset() {
    setDdi('55');
    setPhoneDisplay('');
    setContactInput('');
    setSelectedContact(null);
    setWhatsappNumberId(null);
    setDepartmentId(null);
    setBody('');
    setSelectedTemplate(null);
    setHeaderMedia(null);
    setVariables({});
    setError(null);
    setExistingTicketId(null);
    setVerChatLiberado(true);
    setShowDropdown(false);
    setIsCheckingPhone(false);
  }

  const corpoRef = useRef<HTMLDivElement>(null);
  // Mostra o erro e rola o formulário até ele (topo) A CADA tentativa — com
  // o mesmo erro de antes, o aviso já estava lá mas escondido pela rolagem.
  function mostrarErro(msg: string): void {
    setError(msg);
    requestAnimationFrame(() => corpoRef.current?.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  if (!open) return null;

  function handlePhoneInput(raw: string) {
    const digits = raw.replace(/\D/g, '');
    setPhoneDisplay(maskPhone(digits, ddi));
  }

  function handleDdiChange(newDdi: string) {
    setDdi(newDdi);
    // re-aplica máscara pra novo DDI
    setPhoneDisplay(maskPhone(phoneDigits, newDdi));
  }

  function selectContact(c: ContactRow) {
    setSelectedContact({ id: c.id, name: c.display_name ?? '' });
    setContactInput(c.display_name ?? '');
    setShowDropdown(false);

    // Auto-popula telefone do primary_identifier (E.164 sem +). Sem isso o
    // submit falhava com "Número inválido" pq phoneDigits ficava vazio.
    // Heurística: se começa com o DDI atual, strip; senão BR (55) é fallback
    // pq é o caso dominante.
    const raw = c.primary_identifier?.value;
    if (raw && /^\d+$/.test(raw)) {
      if (raw.startsWith(ddi)) {
        setPhoneDisplay(maskPhone(raw.slice(ddi.length), ddi));
      } else if (raw.startsWith('55') && raw.length >= 12 && raw.length <= 13) {
        setDdi('55');
        setPhoneDisplay(maskPhone(raw.slice(2), '55'));
      } else {
        // DDI desconhecido — mantém DDI atual e coloca tudo como número.
        // Usuário ajusta DDI se necessário.
        setPhoneDisplay(maskPhone(raw, ddi));
      }
    }
  }

  function clearSelectedContact() {
    setSelectedContact(null);
    setContactInput('');
    // Limpa o número também — vinha auto-populado do contato selecionado;
    // ao desfazer a seleção, o usuário pode digitar outro contato/número.
    setPhoneDisplay('');
  }

  async function submit() {
    setError(null);

    if (!/^\d{10,15}$/.test(waId)) {
      return mostrarErro('Número inválido — verifique DDI + DDD + número (10-15 dígitos)');
    }
    if (!selectedChannel) return mostrarErro('Selecione o canal de envio');
    if (!departmentId) return mostrarErro('Selecione o departamento');

    // Para QR Code com contato novo: verifica se número tem WhatsApp antes de criar
    if (selectedChannel.channelType === 'qr_code' && selectedContact === null) {
      setIsCheckingPhone(true);
      try {
        const { exists, skipped } = await checkWhatsAppNumber(selectedChannel.id, waId);
        if (!skipped && !exists) {
          mostrarErro('Este número não possui WhatsApp cadastrado.');
          return;
        }
      } catch {
        // Falha na verificação — permite prosseguir (canal pode estar offline)
      } finally {
        setIsCheckingPhone(false);
      }
    }

    // Mensagem inicial — apenas meta_official exige template fora da janela 24h
    // Para qr_code: mensagem é opcional
    let initialMessage: ActiveOutboundInput['initial_message'];

    if (selectedChannel.channelType === 'meta_official') {
      if (selectedTemplate) {
        for (let i = 1; i <= selectedTemplate.variableCount; i++) {
          if (!variables[String(i)]?.trim()) {
            return mostrarErro(`Preencha a variável {{${i}}} do template`);
          }
        }
        // Botões de URL dinâmica precisam do valor do link, senão a Meta recusa
        // com 131008 (parâmetro faltando). Chave `btn:<index>` — separada das
        // variáveis do body pra não sobrescrever uma pela outra.
        const botoesDin = botoesDinamicosDeUrl(selectedTemplate.components);
        for (const b of botoesDin) {
          if (!variables[`btn:${b.index}`]?.trim()) {
            return mostrarErro(`Preencha o link do botão "${b.text}"`);
          }
        }
        // Cabeçalho de mídia exige o arquivo em todo envio.
        if (headerMediaFormatOf(selectedTemplate) && headerMedia == null) {
          return mostrarErro('Escolha a mídia do cabeçalho do template');
        }
        const temVariaveis = selectedTemplate.variableCount > 0 || botoesDin.length > 0;
        initialMessage = {
          type: 'template',
          template_id: selectedTemplate.id,
          ...(temVariaveis && { variables }),
          ...(headerMedia && { header_media_id: headerMedia.id }),
        };
      }
      // Se não selecionou template, deixa sem mensagem (backend aceitará, mas pode rejeitar se fora da janela 24h)
    } else if (selectedChannel.channelType === 'qr_code') {
      if (body.trim()) {
        initialMessage = { type: 'text', body: body.trim() };
      }
      // mensagem vazia = sem initial_message (ticket abre sem msg)
    }

    // Nome do contato: se selecionou existente usa contact_id;
    // se digitou texto usa como nome; se campo vazio usa o número como fallback.
    const newName = contactInput.trim() || `+${waId}`;

    mut.mutate({
      whatsapp_number_id: selectedChannel.id,
      department_id: departmentId,
      ...(selectedContact
        ? { contact_id: selectedContact.id }
        : { new_contact: { name: newName, wa_id: waId } }),
      ...(initialMessage && { initial_message: initialMessage }),
    });
  }

  const contacts = contactsQ.data?.contacts ?? [];
  const isOfficial = selectedChannel?.channelType === 'meta_official';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="flex max-h-[90vh] w-[520px] max-w-[92vw] flex-col rounded-lg bg-n-0 shadow-lg">
        {/* Header */}
        <div className="flex flex-shrink-0 items-center justify-between px-6 pt-6">
          <h2 className="font-bold text-[16px] text-n-900">Novo atendimento</h2>
          <button
            type="button"
            onClick={fechar}
            className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <div
          ref={corpoRef}
          className="mt-5 flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto px-6 pb-2"
        >
          {/* Erros no TOPO — com template grande, embaixo ficava fora da tela. */}
          {error && (
            existingTicketId ? (
              <div className="flex items-start gap-3 rounded-md border border-warning-200 bg-warning-50 px-3 py-3">
                <AlertCircle size={14} className="mt-0.5 shrink-0 text-warning-600" />
                <div className="flex-1">
                  <p className="text-[12px] font-semibold text-warning-800">
                    Chat atribuído
                  </p>
                  <p className="mt-0.5 text-[12px] text-warning-700">{error}</p>
                </div>
                {verChatLiberado && (
                  <button
                    type="button"
                    onClick={() => {
                      onCreated(existingTicketId);
                      reset();
                    }}
                    className="flex shrink-0 items-center gap-1 rounded-md bg-warning-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-warning-700"
                  >
                    Ver chat <ArrowRight size={11} />
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                {error}
              </div>
            )
          )}
          {/* 1. Telefone com DDI */}
          <Field label="Número do cliente com DDD">
            <div className="flex rounded-md border border-n-200 bg-n-0 focus-within:border-brand-500 overflow-hidden">
              <select
                value={ddi}
                onChange={(e) => handleDdiChange(e.target.value)}
                className="shrink-0 border-r border-n-200 bg-n-50 px-2 py-2 text-[13px] text-n-700 outline-none cursor-pointer"
              >
                {DDI_LIST.map((d) => (
                  <option key={d.code} value={d.code}>
                    +{d.code} {d.label}
                  </option>
                ))}
              </select>
              <input
                value={phoneDisplay}
                onChange={(e) => handlePhoneInput(e.target.value)}
                className="flex-1 px-3 py-2 text-[13px] text-n-900 outline-none bg-n-0"
                placeholder={ddi === '55' ? '(11) 96123-4567' : '999999999'}
                inputMode="numeric"
                autoFocus
              />
            </div>
            {phoneDigits.length > 0 && (
              <span className="text-[11px] text-n-400">E.164: +{waId}</span>
            )}
          </Field>

          {/* 2. Busca / criação de contato */}
          <Field label="Nome do contato">
            <div ref={searchRef} className="relative">
              {selectedContact ? (
                // Contato existente selecionado — badge com X para desfazer
                <div className="flex items-center gap-2 rounded-md border border-brand-200 bg-brand-50 px-3 py-2">
                  <span className="flex-1 text-[13px] font-medium text-brand-800">
                    {selectedContact.name}
                  </span>
                  <span className="text-[11px] text-brand-500">contato existente</span>
                  <button
                    type="button"
                    onClick={clearSelectedContact}
                    className="text-brand-400 hover:text-brand-700"
                    aria-label="Remover"
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-n-400" />
                    <input
                      value={contactInput}
                      onChange={(e) => {
                        setContactInput(e.target.value);
                        setSelectedContact(null);
                        setShowDropdown(true);
                      }}
                      onFocus={() => contactInput.length >= 2 && setShowDropdown(true)}
                      className="w-full rounded-md border border-n-200 bg-n-0 py-2 pl-8 pr-3 text-[13px] text-n-900 outline-none focus:border-brand-500"
                      placeholder="Nome ou buscar contato existente…"
                    />
                    {contactsQ.isFetching && (
                      <Loader2 size={12} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-n-400" />
                    )}
                  </div>

                  {/* Dropdown de sugestões */}
                  {showDropdown && contactInput.length >= 2 && contacts.length > 0 && (
                    // A altura segura ~6 itens e o resto rola. Sem isto, a
                    // lista crescia por baixo do modal e os últimos ficavam
                    // inalcançáveis.
                    <div className="absolute z-10 mt-1 max-h-[264px] w-full overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-md">
                      {contacts.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => selectContact(c)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-n-50"
                        >
                          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700">
                            {(c.display_name ?? '?').charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-[13px] font-medium text-n-900">
                              {c.display_name ?? '—'}
                            </div>
                            {c.primary_identifier && (
                              <div className="truncate text-[11px] text-n-400">
                                {c.primary_identifier.value}
                              </div>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </Field>

          {/* 3. Canal de envio */}
          <Field label="Canal de envio">
            {!channelsQ.isLoading && (channelsQ.data ?? []).length === 0 ? (
              <div className="flex items-start gap-2 rounded-md bg-warning-50 px-3 py-2 text-[12px] text-warning-700">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <div>
                  Você não tem nenhum canal liberado pra iniciar atendimento. Peça pro
                  admin vincular um canal ao seu usuário (Configurações → Usuários).
                </div>
              </div>
            ) : (
            <div className="relative">
              <select
                value={whatsappNumberId ?? ''}
                onChange={(e) => {
                  setWhatsappNumberId(e.target.value ? Number(e.target.value) : null);
                  setSelectedTemplate(null);
                  setVariables({});
                  setBody('');
                }}
                className="w-full appearance-none rounded-md border border-n-200 bg-n-0 px-3 py-2 pr-8 text-[13px] text-n-900 outline-none focus:border-brand-500 disabled:opacity-50"
                disabled={channelsQ.isLoading}
              >
                <option value="">Selecione…</option>
                {(channelsQ.data ?? []).map((c: StartableChannel) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName ?? c.displayNumber} ({c.displayNumber}) ·{' '}
                    {c.channelType === 'meta_official' ? 'API Oficial' : 'API QR Code'}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-n-400" />
            </div>
            )}
          </Field>

          {/* 4. Departamento (obrigatório — controle de acesso) */}
          <Field label="Departamento">
            {!isSupport && userDeptIds.length === 0 ? (
              <div className="flex items-start gap-2 rounded-md bg-warning-50 px-3 py-2 text-[12px] text-warning-700">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <div>
                  Você não está vinculado a nenhum departamento. Peça pro admin
                  te adicionar em pelo menos um departamento pra abrir atendimentos.
                </div>
              </div>
            ) : availableDepts.length === 0 && deptsQ.isLoading ? (
              <div className="flex items-center gap-2 rounded-md border border-n-200 bg-n-50 px-3 py-2 text-[12px] text-n-600">
                <Loader2 size={12} className="animate-spin" />
                Carregando departamentos…
              </div>
            ) : availableDepts.length === 0 ? (
              // Support (ou tenant) sem NENHUM departamento cadastrado.
              <div className="flex items-start gap-2 rounded-md bg-warning-50 px-3 py-2 text-[12px] text-warning-700">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                <div>
                  Nenhum departamento cadastrado neste cliente. Crie um em
                  Configurações → Departamentos pra abrir atendimentos.
                </div>
              </div>
            ) : (
              <div className="relative">
                <select
                  value={departmentId ?? ''}
                  onChange={(e) =>
                    setDepartmentId(e.target.value ? Number(e.target.value) : null)
                  }
                  className="w-full appearance-none rounded-md border border-n-200 bg-n-0 px-3 py-2 pr-8 text-[13px] text-n-900 outline-none focus:border-brand-500"
                >
                  <option value="">Selecione…</option>
                  {availableDepts.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-n-400"
                />
              </div>
            )}
          </Field>

          {/* 5. Mensagem — sempre visível; muda conteúdo pelo tipo de canal */}
          {isOfficial ? (
            <TemplateComposer
              templates={templatesQ.data ?? []}
              isLoading={templatesQ.isLoading}
              selected={selectedTemplate}
              onSelect={(t) => {
                setSelectedTemplate(t);
                setHeaderMedia(null);
                setVariables({});
              }}
              variables={variables}
              onVariablesChange={setVariables}
              headerMedia={headerMedia}
              onHeaderMediaChange={setHeaderMedia}
            />
          ) : (
            <Field label="Mensagem inicial (opcional)">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="min-h-[88px] resize-y rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
                placeholder="Olá! Aqui é o atendimento da empresa…"
              />
              <span className="text-[11px] text-n-400">
                Deixe em branco para abrir o atendimento sem mensagem inicial.
              </span>
            </Field>
          )}
        </div>

        {/* Rodapé */}
        <div className="flex flex-shrink-0 justify-end gap-2 border-t border-n-100 px-6 py-4">
          <button
            type="button"
            onClick={fechar}
            className="rounded-md border border-n-200 bg-n-0 px-4 py-2 font-semibold text-[12px] text-n-700 hover:bg-n-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => { void submit(); }}
            disabled={mut.isPending || isCheckingPhone}
            className="flex items-center gap-2 rounded-md bg-brand-500 px-4 py-2 font-bold text-[12px] text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {(mut.isPending || isCheckingPhone) && <Loader2 size={12} className="animate-spin" />}
            {isCheckingPhone ? 'Verificando...' : 'Criar atendimento'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- TemplateComposer (inalterado da versão anterior) ----------

interface TemplateComposerProps {
  templates: TemplateRow[];
  isLoading: boolean;
  selected: TemplateRow | null;
  onSelect: (t: TemplateRow) => void;
  variables: Record<string, string>;
  onVariablesChange: (v: Record<string, string>) => void;
  headerMedia: LibraryMedia | null;
  onHeaderMediaChange: (m: LibraryMedia | null) => void;
}

function TemplateComposer({
  templates,
  isLoading,
  selected,
  onSelect,
  variables,
  onVariablesChange,
  headerMedia,
  onHeaderMediaChange,
}: TemplateComposerProps) {
  const formatoDoCabecalho = selected ? headerMediaFormatOf(selected) : null;
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-n-200 bg-n-50 px-3 py-3 text-[12px] text-n-600">
        <Loader2 size={14} className="animate-spin" />
        Carregando templates HSM aprovados…
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 px-3 py-3 text-[12px] text-warning-700">
        <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
        <div>
          <strong>Nenhum template HSM aprovado.</strong>
          <div className="mt-0.5">
            Sincronize em <span className="font-mono">Configurações → Templates</span> ou crie no Meta Business Manager.
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Field label="Template HSM (opcional — exigido apenas fora da janela 24h)">
        <select
          value={selected?.id ?? ''}
          onChange={(e) => {
            const t = templates.find((x) => x.id === Number(e.target.value));
            if (t) onSelect(t);
          }}
          className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
        >
          <option value="">Selecione um template…</option>
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} · {t.language} · {t.category}
            </option>
          ))}
        </select>
      </Field>

      {selected && (
        <div className="flex flex-col gap-2 rounded-md border border-n-200 bg-n-50 px-3 py-3">
          {/* Mídia do cabeçalho primeiro — é o que abre a mensagem no WhatsApp.
              Da galeria ou enviada do computador na hora. */}
          {formatoDoCabecalho && (
            <MidiaDoCabecalhoPicker
              type={formatoDoCabecalho}
              selectedId={headerMedia?.id ?? null}
              selectedLabel={headerMedia?.label ?? null}
              selectedMime={headerMedia?.mime_type ?? null}
              onSelect={(m) => onHeaderMediaChange(m)}
              onClear={() => onHeaderMediaChange(null)}
            />
          )}
          {/* Variáveis ANTES do preview (igual campanhas) — preenche e vê o
              resultado renderizado logo abaixo. */}
          {selected.variableCount > 0 && (
            <div className="flex flex-col gap-2">
              <span className="font-semibold text-[11px] uppercase tracking-[0.05em] text-n-500">
                Variáveis
              </span>
              {Array.from({ length: selected.variableCount }, (_, i) => i + 1).map((n) => (
                <label key={n} className="flex flex-col gap-1">
                  <span className="font-mono text-[11px] text-n-600">{`{{${n}}}`}</span>
                  <input
                    value={variables[String(n)] ?? ''}
                    onChange={(e) => onVariablesChange({ ...variables, [String(n)]: e.target.value })}
                    className="rounded-md border border-n-200 bg-n-0 px-2 py-1 text-[12px]"
                    placeholder={`Conteúdo da variável ${n}`}
                  />
                </label>
              ))}
            </div>
          )}
          {/* Link dos botões de URL dinâmica — o rótulo do botão é fixo, mas o
              destino tem `{{N}}` que a Meta exige preencher no envio. */}
          <BotaoUrlInputs
            template={selected}
            variables={variables}
            onVariablesChange={onVariablesChange}
          />
          <span className="font-semibold text-[11px] uppercase tracking-[0.05em] text-n-500">
            Pré-visualização
          </span>
          {/* Bolha verde estilo WhatsApp (igual campanha). force-light: verde
              fixo não inverte no dark. */}
          <div className="force-light rounded-md bg-[#dcf8c6] px-3 py-2 text-[12px] text-n-900 shadow-sm">
            {/* Cabeçalho de mídia (imagem/vídeo/documento) antes do texto —
                mostra o que realmente vai ser enviado. */}
            <PreviewHeaderMedia template={selected} mediaId={headerMedia?.id ?? null} />
            <div className="whitespace-pre-wrap">{renderPreview(selected, variables)}</div>
            <PreviewButtons template={selected} />
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Formato do cabeçalho de MÍDIA do template (IMAGE/VIDEO/DOCUMENT), ou null se
 * o header for de texto/inexistente. Templates com header de mídia exigem o
 * arquivo em TODO envio — escolhido no próprio modal (galeria ou upload).
 */
function headerMediaFormatOf(template: TemplateRow): 'image' | 'video' | 'document' | null {
  const comps = (template.components ?? []) as Array<{ type?: string; format?: string }>;
  const header = comps.find((c) => (c.type ?? '').toUpperCase() === 'HEADER');
  const fmt = (header?.format ?? 'TEXT').toUpperCase();
  if (fmt === 'IMAGE') return 'image';
  if (fmt === 'VIDEO') return 'video';
  if (fmt === 'DOCUMENT') return 'document';
  return null;
}

/**
 * Cabeçalho de mídia na pré-visualização: mostra a imagem que será enviada
 * (resolvendo a signed URL) ou avisa que falta configurar — sem isso o envio
 * falha na Meta e o atendente não saberia o motivo.
 */
function PreviewHeaderMedia({
  template,
  mediaId,
}: {
  template: TemplateRow;
  /** Mídia escolhida pra este envio (galeria ou arquivo do computador). */
  mediaId: number | null;
}) {
  const format = headerMediaFormatOf(template);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (format == null || mediaId == null) {
      setUrl(null);
      return;
    }
    let alive = true;
    getMediaUrlById(mediaId)
      .then((r) => {
        if (alive) setUrl(r.url);
      })
      .catch(() => {
        if (alive) setUrl(null);
      });
    return () => {
      alive = false;
    };
  }, [format, mediaId]);

  if (format == null) return null;

  if (mediaId == null) {
    return (
      <div className="mb-1.5 flex items-start gap-1.5 rounded bg-warning-50 px-2 py-1.5 text-[11px] text-warning-700">
        <AlertCircle size={13} className="mt-px flex-shrink-0" />
        <span>
          Este template tem cabeçalho de{' '}
          {format === 'image' ? 'imagem' : format === 'video' ? 'vídeo' : 'documento'}. Escolha a
          mídia acima — da Biblioteca ou enviando do computador.
        </span>
      </div>
    );
  }

  if (format === 'image') {
    return url ? (
      <img
        src={url}
        alt="Cabeçalho do template"
        className="mb-1.5 max-h-[160px] w-full rounded object-cover"
      />
    ) : (
      <div className="mb-1.5 flex h-[80px] items-center justify-center rounded bg-black/5">
        <Loader2 size={16} className="animate-spin text-n-400" />
      </div>
    );
  }

  // Vídeo/documento: some sem preview visual — indica que vai junto.
  return (
    <div className="mb-1.5 rounded bg-black/5 px-2 py-1.5 text-[11px] text-n-600">
      📎 {format === 'video' ? 'Vídeo' : 'Documento'} no cabeçalho
    </div>
  );
}

function renderPreview(template: TemplateRow, variables: Record<string, string>): string {
  const text = (template.components as Array<{ text?: string }>)
    .map((c) => c.text ?? '')
    .filter(Boolean)
    .join('\n\n');
  return text.replace(/\{\{(\d+)\}\}/g, (_, n) => variables[n] || `{{${n}}}`);
}

const ICONE_BOTAO = { url: ExternalLink, phone_number: Phone } as const;

/**
 * Os botões do template na prévia — como o cliente vê no WhatsApp: bloco branco
 * abaixo da bolha, rótulo azul, ícone por tipo. Só o RÓTULO aparece (o WhatsApp
 * não mostra a URL); o destino dinâmico é coletado em `BotaoUrlInputs`.
 */
function PreviewButtons({ template }: { template: TemplateRow }) {
  const botoes = botoesDoTemplate(template.components);
  if (botoes.length === 0) return null;
  return (
    <div className="-mx-3 mt-1.5 border-t border-black/10">
      {botoes.map((b: BotaoTemplate) => {
        const Icon = ICONE_BOTAO[b.type as keyof typeof ICONE_BOTAO] ?? Reply;
        return (
          <div
            key={`${b.type}-${b.index}`}
            className="flex items-center justify-center gap-1.5 border-black/10 px-3 py-1.5 font-medium text-[12px] text-[#1e88e5] [&:not(:first-child)]:border-t"
          >
            <Icon size={14} strokeWidth={2} />
            {b.text}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Um campo por botão de URL dinâmica, pra o atendente completar o `{{N}}` do
 * link. Guarda em `variables['btn:<index>']` — chave separada das variáveis do
 * body (o `{{1}}` do botão NÃO é o `{{1}}` do texto).
 */
function BotaoUrlInputs({
  template,
  variables,
  onVariablesChange,
}: {
  template: TemplateRow;
  variables: Record<string, string>;
  onVariablesChange: (v: Record<string, string>) => void;
}) {
  const botoes = botoesDinamicosDeUrl(template.components);
  if (botoes.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <span className="font-semibold text-[11px] uppercase tracking-[0.05em] text-n-500">
        Link dos botões
      </span>
      {botoes.map((b) => {
        const key = `btn:${b.index}`;
        return (
          <label key={b.index} className="flex flex-col gap-1">
            <span className="text-[11px] text-n-600">
              Botão <strong>{b.text}</strong>
            </span>
            <div className="flex items-center overflow-hidden rounded-md border border-n-200 bg-n-0">
              <span className="max-w-[52%] shrink-0 truncate border-n-200 border-r bg-n-50 px-2 py-1 font-mono text-[11px] text-n-400">
                {prefixoDaUrl(b.url)}
              </span>
              <input
                value={variables[key] ?? ''}
                onChange={(e) => onVariablesChange({ ...variables, [key]: e.target.value })}
                className="flex-1 bg-n-0 px-2 py-1 text-[12px] outline-none"
                placeholder="valor do link"
              />
            </div>
          </label>
        );
      })}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-semibold text-[12px] text-n-700">{label}</span>
      {children}
    </label>
  );
}
